/*eslint max-lines-per-function: ["error", 400]*/
import { DefaultApi } from '../autogenerated/spdApiClient/apis/DefaultApi';
import {
  GetExamDto,
  LabelValue,
  LabelWith2ValuesValue,
  LabelWith3ValuesValue,
  LabelWith4CountsValue,
  OverdueInvoiceDebtDto,
  RegisterStatistics,
  StatisticsSPD,
  TopCompanyInvoicesDto,
  TopCompanyJudgmentsDto,
  UnlinkedPaymentsTotalDto,
} from '../autogenerated/spdApiClient';
import { GridRecord } from '../../components/Grid/GridDataTypes';
import { reportJudgmentForGrid } from '../../../spd/components/Reports/ReportJudgmentsSearch';
import { reportConsultationForGrid } from '../../../spd/components/Reports/ReportConsultationsSearch';
import { reportCompanyForGrid } from '../../../spd/components/Reports/ReportCompaniesSearch';
import { judgmentForGrid } from '../../../spd/components/Judgments/JudgmentsSearch';
import { reportSynevoCompanyForGrid } from '../../../spd/components/Reports/ReportSynevoCompaniesSearch';

export type ExamEntity = GetExamDto;

export interface ReportContextInterface {
  getJudgmentsByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    dateFrom?: Date,
    dateTo?: Date,
    userUid?: string,
    companyId?: string,
    healthcareFacilityId?: string,
    withHealthcareFacility?: boolean,
    withRegister?: boolean,
  ) => Promise<GridRecord[]>;
  getJudgmentsCount: (
    filterText?: string,
    dateFrom?: Date,
    dateTo?: Date,
    userUid?: string,
    companyId?: string,
    healthcareFacilityId?: string,
    withHealthcareFacility?: boolean,
    withRegister?: boolean,
  ) => Promise<number>;
  getConsultationsByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    dateFrom?: Date,
    dateTo?: Date,
    userUid?: string,
    companyId?: string,
    healthcareFacilityId?: string,
    withHealthcareFacility?: boolean,
    withType?: boolean,
  ) => Promise<GridRecord[]>;
  getConsultationsCount: (
    filterText?: string,
    dateFrom?: Date,
    dateTo?: Date,
    userUid?: string,
    companyId?: string,
    healthcareFacilityId?: string,
    withHealthcareFacility?: boolean,
    withType?: boolean,
  ) => Promise<number>;
  getCompaniesByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    dateFrom?: Date,
    dateTo?: Date,
    onlyMonthlySettlement?: boolean,
    companyId?: string,
    healthcareFacilityId?: string,
    withHealthcareFacility?: boolean,
    withRegister?: boolean,
  ) => Promise<GridRecord[]>;
  getCompaniesCount: (
    filterText?: string,
    dateFrom?: Date,
    dateTo?: Date,
    onlyMonthlySettlement?: boolean,
    companyId?: string,
    healthcareFacilityId?: string,
    withHealthcareFacility?: boolean,
    withRegister?: boolean,
  ) => Promise<number>;
  getSynevoCompaniesByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    dateFrom?: Date,
    dateTo?: Date,
    companyId?: string,
  ) => Promise<GridRecord[]>;
  getSynevoCompaniesCount: (
    filterText?: string,
    dateFrom?: Date,
    dateTo?: Date,
    companyId?: string,
  ) => Promise<number>;
  getCompaniesDetailsByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    psychologistId?: string,
    companyId?: string,
    registerId?: string,
    healthcareFacilityId?: string,
    dateFrom?: Date,
    dateTo?: Date,
    onlyMonthlySettlement?: string,
    withHealthcareFacility?: boolean,
    withRegister?: boolean,
  ) => Promise<GridRecord[]>;
  getCompaniesDetailsCount: (
    filterText?: string,
    psychologistId?: string,
    companyId?: string,
    registerId?: string,
    healthcareFacilityId?: string,
    dateFrom?: Date,
    dateTo?: Date,
    onlyMonthlySettlement?: string,
    withHealthcareFacility?: boolean,
    withRegister?: boolean,
  ) => Promise<number>;
  getJudgmentsByCreatorByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    dateFrom?: Date,
    dateTo?: Date,
  ) => Promise<GridRecord[]>;
  getJudgmentsByCreatorCount: (
    filterText?: string,
    dateFrom?: Date,
    dateTo?: Date,
  ) => Promise<number>;
  reportJudgments: (
    currentMonthOnly?: boolean,
    dateFrom?: Date,
    dateTo?: Date,
    companyId?: string,
  ) => Promise<LabelValue[]>;
  reportCloseExams: (
    currentMonthOnly?: boolean,
    dateFrom?: Date,
    dateTo?: Date,
    companyId?: string,
  ) => Promise<LabelValue[]>;
  reportByTypeExams: (
    currentMonthOnly?: boolean,
    dateFrom?: Date,
    dateTo?: Date,
    companyId?: string,
  ) => Promise<LabelWith4CountsValue[]>;
  reportInvoicesReceipts: (
    currentMonthOnly?: boolean,
    dateFrom?: Date,
    dateTo?: Date,
    companyId?: string,
  ) => Promise<LabelWith2ValuesValue[]>;
  reportStatisticsSPDCounts: (
    dateFrom?: Date,
    dateTo?: Date,
  ) => Promise<StatisticsSPD>;
  reportRegisterStatisticsSPDByMonthsCounts: (
    rangeFrom?: Date,
    rangeTo?: Date,
    userUid?: string,
    companyId?: string,
  ) => Promise<RegisterStatistics>;
  reportOverdueInvoices: () => Promise<OverdueInvoiceDebtDto[]>;
  reportTopCompaniesByJudgments: (
    dateFrom?: Date,
    dateTo?: Date,
  ) => Promise<TopCompanyJudgmentsDto[]>;
  reportTopCompaniesByInvoices: (
    dateFrom?: Date,
    dateTo?: Date,
  ) => Promise<TopCompanyInvoicesDto[]>;
  reportUnlinkedPaymentsTotal: () => Promise<UnlinkedPaymentsTotalDto>;
  reportPaymentsByDate: (
    currentMonthOnly?: boolean,
    dateFrom?: Date,
    dateTo?: Date,
  ) => Promise<LabelWith3ValuesValue[]>;
  reportInvoicesReceiptsByPayment: (
    currentMonthOnly?: boolean,
    dateFrom?: Date,
    dateTo?: Date,
    companyId?: string,
  ) => Promise<LabelWith2ValuesValue[]>;
}

export const ReportContext = (api: DefaultApi) => {
  return {
    getJudgmentsByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      dateFrom?: Date,
      dateTo?: Date,
      userUid?: string,
      companyId?: string,
      healthcareFacilityId?: string,
      withHealthcareFacility?: boolean,
      withRegister?: boolean,
    ) => {
      const data = await api.reportControllerGetJudgmentsByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        dateFrom,
        dateTo,
        userUid,
        companyId,
        healthcareFacilityId,
        withHealthcareFacility,
        withRegister,
      );
      return data.map(judgment =>
        reportJudgmentForGrid(
          judgment,
          companyId,
          dateFrom,
          dateTo,
          withHealthcareFacility,
          withRegister,
        ),
      );
    },

    getJudgmentsCount: (
      filterText?: string,
      dateFrom?: Date,
      dateTo?: Date,
      userUid?: string,
      companyId?: string,
      healthcareFacilityId?: string,
      withHealthcareFacility?: boolean,
      withRegister?: boolean,
    ) =>
      api.reportControllerGetJudgmentsCount(
        filterText || '',
        dateFrom,
        dateTo,
        userUid,
        companyId,
        healthcareFacilityId,
        withHealthcareFacility,
        withRegister,
      ),
    getConsultationsByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      dateFrom?: Date,
      dateTo?: Date,
      userUid?: string,
      companyId?: string,
      healthcareFacilityId?: string,
      withHealthcareFacility?: boolean,
      withType?: boolean,
    ) => {
      const data = await api.reportControllerGetConsultationsByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        dateFrom,
        dateTo,
        userUid,
        companyId,
        healthcareFacilityId,
        withHealthcareFacility,
        withType,
      );
      return data.map(consultation => reportConsultationForGrid(consultation));
    },

    getConsultationsCount: (
      filterText?: string,
      dateFrom?: Date,
      dateTo?: Date,
      userUid?: string,
      companyId?: string,
      healthcareFacilityId?: string,
      withHealthcareFacility?: boolean,
      withType?: boolean,
    ) =>
      api.reportControllerGetConsultationsCount(
        filterText || '',
        dateFrom,
        dateTo,
        userUid,
        companyId,
        healthcareFacilityId,
        withHealthcareFacility,
        withType,
      ),

    getCompaniesByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      dateFrom?: Date,
      dateTo?: Date,
      onlyMonthlySettlement?: boolean,
      companyId?: string,
      healthcareFacilityId?: string,
      withHealthcareFacility?: boolean,
      withRegister?: boolean,
    ) => {
      const data = await api.reportControllerGetCompaniesByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        dateFrom,
        dateTo,
        onlyMonthlySettlement,
        companyId,
        healthcareFacilityId,
        withHealthcareFacility,
        withRegister,
      );
      return data.map(company =>
        reportCompanyForGrid(company, dateFrom, dateTo, onlyMonthlySettlement),
      );
    },

    getCompaniesCount: (
      filterText?: string,
      dateFrom?: Date,
      dateTo?: Date,
      onlyMonthlySettlement?: boolean,
      companyId?: string,
      healthcareFacilityId?: string,
      withHealthcareFacility?: boolean,
      withRegister?: boolean,
    ) =>
      api.reportControllerGetCompaniesCount(
        filterText || '',
        dateFrom,
        dateTo,
        onlyMonthlySettlement,
        companyId,
        healthcareFacilityId,
        withHealthcareFacility,
        withRegister,
      ),

    getSynevoCompaniesByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      dateFrom?: Date,
      dateTo?: Date,
      companyId?: string,
    ) => {
      const data = await api.reportControllerGetSynevoCompaniesByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        dateFrom,
        dateTo,
        companyId,
      );
      return data.map(company =>
        //reportSynevoCompanyForGrid(company, dateFrom, dateTo),
        reportSynevoCompanyForGrid(company),
      );
    },

    getSynevoCompaniesCount: (
      filterText?: string,
      dateFrom?: Date,
      dateTo?: Date,
      companyId?: string,
    ) =>
      api.reportControllerGetSynevoCompaniesCount(
        filterText || '',
        dateFrom,
        dateTo,
        companyId,
      ),

    getCompaniesDetailsByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      psychologistId?: string,
      companyId?: string,
      registerId?: string,
      healthcareFacilityId?: string,
      dateFrom?: Date,
      dateTo?: Date,
      onlyMonthlySettlement?: string,
      withHealthcareFacility?: boolean,
      withRegister?: boolean,
    ) => {
      const data = await api.reportControllerGetCompaniesDetailsByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        psychologistId,
        companyId,
        registerId,
        healthcareFacilityId,
        dateFrom,
        dateTo,
        onlyMonthlySettlement,
        withHealthcareFacility,
        withRegister,
      );
      return data.map(det => judgmentForGrid(det));
    },
    getCompaniesDetailsCount: (
      filterText?: string,
      psychologistId?: string,
      companyId?: string,
      registerId?: string,
      healthcareFacilityId?: string,
      dateFrom?: Date,
      dateTo?: Date,
      onlyMonthlySettlement?: string,
      withHealthcareFacility?: boolean,
      withRegister?: boolean,
    ) =>
      api.reportControllerGetCompaniesDetailsCount(
        filterText || '',
        psychologistId,
        companyId,
        registerId,
        healthcareFacilityId,
        dateFrom,
        dateTo,
        onlyMonthlySettlement,
        withHealthcareFacility,
        withRegister,
      ),

    getJudgmentsByCreatorByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      dateFrom?: Date,
      dateTo?: Date,
    ) => {
      const data = await api.reportControllerGetJudgmentsByCreatorByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        dateFrom,
        dateTo,
      );
      return data.map(judgment =>
        reportJudgmentForGrid(judgment, undefined, dateFrom, dateTo),
      );
    },

    getJudgmentsByCreatorCount: (
      filterText?: string,
      dateFrom?: Date,
      dateTo?: Date,
    ) =>
      api.reportControllerGetJudgmentsByCreatorCount(
        filterText || '',
        dateFrom,
        dateTo,
      ),

    reportJudgments: (
      currentMonthOnly?: boolean,
      dateFrom?: Date,
      dateTo?: Date,
      companyId?: string,
    ) =>
      api.reportControllerReportJudgments(
        currentMonthOnly,
        dateFrom,
        dateTo,
        companyId,
      ),
    reportCloseExams: (
      currentMonthOnly?: boolean,
      dateFrom?: Date,
      dateTo?: Date,
      companyId?: string,
    ) =>
      api.reportControllerReportCloseExams(
        currentMonthOnly,
        dateFrom,
        dateTo,
        companyId,
      ),

    reportByTypeExams: (
      currentMonthOnly?: boolean,
      dateFrom?: Date,
      dateTo?: Date,
      companyId?: string,
    ) =>
      api.reportControllerReportByTypeExams(
        currentMonthOnly,
        dateFrom,
        dateTo,
        companyId,
      ),
    reportInvoicesReceipts: (
      currentMonthOnly?: boolean,
      dateFrom?: Date,
      dateTo?: Date,
      companyId?: string,
    ) =>
      api.reportControllerReportInvoicesReceipts(
        currentMonthOnly,
        dateFrom,
        dateTo,
        companyId,
      ),
    reportStatisticsSPDCounts: (dateFrom?: Date, dateTo?: Date) =>
      api.reportControllerReportStatisticsSPDCounts(dateFrom, dateTo),
    reportRegisterStatisticsSPDByMonthsCounts: (
      rangeFrom?: Date,
      rangeTo?: Date,
      userUid?: string,
      companyId?: string,
    ) =>
      api.reportControllerReportRegisterStatisticsSPDByMonthsCounts(
        rangeFrom,
        rangeTo,
        userUid,
        companyId,
      ),
    reportOverdueInvoices: () => api.reportControllerReportOverdueInvoices(),
    reportTopCompaniesByJudgments: (dateFrom?: Date, dateTo?: Date) =>
      api.reportControllerReportTopCompaniesByJudgments(dateFrom, dateTo),
    reportTopCompaniesByInvoices: (dateFrom?: Date, dateTo?: Date) =>
      api.reportControllerReportTopCompaniesByInvoices(dateFrom, dateTo),
    reportUnlinkedPaymentsTotal: () =>
      api.reportControllerReportUnlinkedPaymentsTotal(),
    reportPaymentsByDate: (
      currentMonthOnly?: boolean,
      dateFrom?: Date,
      dateTo?: Date,
    ) =>
      api.reportControllerReportPaymentsByDate(
        currentMonthOnly,
        dateFrom,
        dateTo,
      ),
    reportInvoicesReceiptsByPayment: (
      currentMonthOnly?: boolean,
      dateFrom?: Date,
      dateTo?: Date,
      companyId?: string,
    ) =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (api as any).reportControllerReportInvoicesReceiptsByPayment(
        currentMonthOnly,
        dateFrom,
        dateTo,
        companyId,
      ),
  };
};
